import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { pedidosApi } from '../services/api';

const MENSAGEM_AMAZON_DBA_PUBLICA = {
  titulo: 'Pedido gerenciado internamente pela Amazon',
  texto: 'O rastreamento desta entrega é feito pela própria Amazon. Acesse o site ou aplicativo da Amazon com o número do seu pedido para acompanhar a entrega.'
};

const parseRastreioResponse = (data) => {
  if (!data) {
    return { amazonDba: null, pedidos: [] };
  }

  const itens = Array.isArray(data) ? data : [data];
  const amazonItem = itens.find((item) => item?.situacao === 'amazon_dba')
    || (data?.situacao === 'amazon_dba' ? data : null);

  if (amazonItem) {
    return { amazonDba: amazonItem, pedidos: [] };
  }

  const pedidos = itens.filter((item) => (
    item?.codigo_marketplace
    || item?.transportadora
    || item?.status
    || item?.data_pedido
    || item?.tracking?.eventos?.length
  ));
  return { amazonDba: null, pedidos };
};

const RastreioPublico = () => {
  const { numero } = useParams();
  const [busca, setBusca] = useState(numero || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resultados, setResultados] = useState([]);
  const [amazonDba, setAmazonDba] = useState(null);

  useEffect(() => {
    if (numero) handleSearch();
  }, [numero]);

  const handleSearch = async () => {
    if (!busca.trim()) return;
    setLoading(true);
    setError('');
    setResultados([]);
    setAmazonDba(null);
    try {
      const data = await pedidosApi.rastreioPublico(busca.trim());
      const parsed = parseRastreioResponse(data);
      setAmazonDba(parsed.amazonDba);
      setResultados(parsed.pedidos);
    } catch (err) {
      setError(err.response?.data?.error || 'Pedido não encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => e.key === 'Enter' && handleSearch();

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const getStatusClass = (status) => {
    const map = {
      'enviado': 'status-enviado',
      'em_transito': 'status-em_transito',
      'em_rota': 'status-em_rota',
      'entregue': 'status-entregue',
      'cancelado': 'status-cancelado',
      'pendente': 'status-pendente'
    };
    return map[status] || 'badge-secondary';
  };

  const translateStatus = (status) => {
    const map = {
      'enviado': 'Enviado',
      'em_transito': 'Em Trânsito',
      'em_rota': 'Em Rota',
      'entregue': 'Entregue',
      'cancelado': 'Cancelado',
      'pendente': 'Pendente'
    };
    return map[status] || status;
  };

  return (
    <div className="rastreio-publico">
      {/* Header com cores WCT */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <a href="https://wct.com.br" className="logo">
              {/* Logo placeholder com as cores - substitua pela imagem real */}
              <div className="logo-placeholder">
                <span className="logo-text">WCT</span>
                <span className="logo-text-light">FITNESS</span>
              </div>
            </a>
            <nav className="header-nav">
              <a target='_blank' href="https://wct.com.br">Loja</a>
              <a target='_blank' href="https://www.wct.com.br/contato">Atendimento</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section com identidade WCT */}
      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <h1>Rastrear pedido</h1>
              <p className="hero-description">
                Monitore a entrega do seu pedido em tempo real utilizando a NF, CPF/CNPJ ou código do pedido
              </p>
              
              <div className="search-box">
                <input
                  type="text"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="NF, CPF/CNPJ ou código do pedido"
                  className="search-input"
                />
                <button 
                  onClick={handleSearch}
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Buscando (pode levar alguns segundos)...' : 'Rastrear'}
                </button>
              </div>

              {error && <div className="alert-error">{error}</div>}
            </div>

            {/* Selos com ícones minimalistas nas cores WCT */}
            <div className="hero-seals">
              <div className="seal-grid">
                <div className="seal-item">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 2L2 7v7c0 5 10 8 10 8s10-3 10-8V7l-10-5z" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 12v4" strokeLinecap="round"/><path d="M12 8h.01"/>
                  </svg>
                  <span>Site Seguro</span>
                </div>
                <div className="seal-item">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="7" width="20" height="14" rx="2" strokeLinecap="round"/>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" strokeLinecap="round"/>
                  </svg>
                  <span>Loja Oficial</span>
                </div>
                <div className="seal-item">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="5" width="20" height="14" rx="2"/>
                    <path d="M2 10h20" strokeLinecap="round"/>
                  </svg>
                  <span>12x sem juros</span>
                </div>
                <div className="seal-item">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 2v4M12 22v-4M4 12H2h2M20 12h2-2M5.6 5.6l-1.4-1.4 1.4 1.4zM18.4 5.6l1.4-1.4-1.4 1.4z" strokeLinecap="round"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <span>Garantia 3 meses</span>
                </div>
                <div className="seal-item">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="6" width="20" height="12" rx="2"/>
                    <path d="M16 10v4M8 10v4M12 10v4M6 6V4h12v2" strokeLinecap="round"/>
                  </svg>
                  <span>Envio rápido</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resultados do Rastreio */}
      {amazonDba && (
        <section className="resultados">
          <div className="container">
            <div className="pedido-card amazon-dba-card">
              <div className="pedido-header">
                <div>
                  <h2>Pedido #{amazonDba.codigo_pedido || busca.trim()}</h2>
                  <p className="pedido-date amazon-dba-subtitulo">{amazonDba.plataforma || 'Amazon'}</p>
                </div>
                <span className="badge status-amazon-dba">Entrega Amazon</span>
              </div>
              <div className="pedido-body">
                <div className="amazon-dba-aviso">
                  <div className="amazon-dba-aviso-icon" aria-hidden="true">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 9v4" strokeLinecap="round"/>
                      <circle cx="12" cy="16" r="0.5" fill="currentColor"/>
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3>{MENSAGEM_AMAZON_DBA_PUBLICA.titulo}</h3>
                    <p>{amazonDba.mensagem || MENSAGEM_AMAZON_DBA_PUBLICA.texto}</p>
                    <p className="amazon-dba-dica">
                      Utilize o mesmo código do pedido informado acima na sua conta Amazon em
                      {' '}<strong>Seus pedidos</strong> para ver o status da entrega.
                    </p>
                  </div>
                </div>
                <div className="info-resumida">
                  <div className="info-item">
                    <span className="info-label">Transportadora</span>
                    <span className="info-value">{amazonDba.transportadora || 'Amazon (entrega própria)'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Rastreio WCT</span>
                    <span className="info-value">Não disponível para este tipo de envio</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {resultados.length > 0 && (
        <section className="resultados">
          <div className="container">
            {resultados.map((pedido, index) => (
              <div key={index} className="pedido-card">
                <div className="pedido-header">
                  <div>
                    <h2>Pedido #{pedido.codigo_marketplace}</h2>
                    <p className="pedido-date">{formatDate(pedido.data_pedido)}</p>
                  </div>
                  <span className={`badge ${getStatusClass(pedido.status)}`}>
                    {translateStatus(pedido.status)}
                  </span>
                </div>
                <div className="pedido-body">
                  {pedido.tracking?.eventos?.length > 0 && (
                    <div className="tracking-section">
                      <h3>Histórico de Rastreamento</h3>
                      <div className="timeline">
                        {pedido.tracking.eventos.map((evento, i) => (
                          <div key={i} className="timeline-item">
                            <div className={`timeline-dot ${i === 0 ? 'success' : ''}`} />
                            <div className="timeline-content">
                              <div className="timeline-date">
                                {formatDate(evento.dataHora)} {evento.dataHora?.split('T')[1]?.substring(0, 5)}
                              </div>
                              <div className="timeline-title">{evento.status}</div>
                              {evento.descricao && <div className="timeline-description">{evento.descricao}</div>}
                              {evento.cidade && <div className="timeline-location">{evento.cidade}{evento.uf && ` - ${evento.uf}`}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="info-resumida">
                    <div className="info-item">
                      <span className="info-label">Transportadora</span>
                      <span className="info-value">{pedido.transportadora || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Destino</span>
                      <span className="info-value">
                        {pedido.endereco?.cidade ? `${pedido.endereco.cidade}${pedido.endereco.uf ? ` - ${pedido.endereco.uf}` : ''}` : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer com identidade WCT */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-section">
              <h4>Institucional</h4>
              <p>XPORT WCT FITNESS LTDA</p>
              <p>17.751.890/0001-76</p>
            </div>
            <div className="footer-section">
              <h4>Atendimento</h4>
              <p>(47) 3432-5055</p>
              <p>(47) 99656-8130</p>
              <p>sac@wct.com.br</p>
              <p className="footer-hours">Seg a sex, 9h às 17h</p>
            </div>
            <div className="footer-section">
              <h4>Pagamentos</h4>
              <div className="payment-methods">
                <span>Cartão</span>
                <span>Boleto</span>
                <span>Pix</span>
              </div>
            </div>
            <div className="footer-section">
              <h4>Segurança</h4>
              <div className="security-seals">
                <span>Google Safe Browsing</span>
                <span>Site Protegido</span>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>Â© 2026 WCT Fitness. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      <style>{`
        .rastreio-publico {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* Header com fundo preto (#171717) */
        .header {
          background: #171717;
          padding: 16px 0;
        }
        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .logo {
          display: flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
        }
        .logo-placeholder {
          display: flex;
          align-items: baseline;
          gap: 4px;
        }
        .logo-text {
          color: #feb200;
          font-weight: 700;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .logo-text-light {
          color: white;
          font-weight: 400;
          font-size: 1.2rem;
          letter-spacing: -0.02em;
        }
        .header-nav {
          display: flex;
          gap: 32px;
        }
        .header-nav a {
          color: white;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }
        .header-nav a:hover {
          color: #feb200;
        }

        /* Hero com gradiente sutil usando as cores */
        .hero {
          background: linear-gradient(135deg, #171717 0%, #2a2a2a 100%);
          color: white;
          padding: 48px 0 64px;
        }
        .hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 48px;
          align-items: center;
        }
        .hero h1 {
          font-size: 2rem;
          font-weight: 600;
          margin-bottom: 16px;
          letter-spacing: -0.02em;
        }
        .hero-description {
          font-size: 1.125rem;
          opacity: 0.9;
          margin-bottom: 32px;
          line-height: 1.6;
        }
        .search-box {
          display: flex;
          gap: 12px;
          background: white;
          padding: 4px;
          border-radius: 8px;
          max-width: 500px;
        }
        .search-input {
          flex: 1;
          padding: 14px 16px;
          border: none;
          border-radius: 6px;
          font-size: 1rem;
          outline: none;
        }
        .btn-primary {
          background: #feb200;
          color: #171717;
          border: none;
          padding: 12px 32px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .btn-primary:hover:not(:disabled) {
          background: #ffc233;
          transform: translateY(-1px);
        }
        .btn-primary:disabled { 
          opacity: 0.6; 
          cursor: not-allowed;
          transform: none;
        }
        .alert-error {
          margin-top: 16px;
          padding: 12px;
          background: rgba(254, 178, 0, 0.1);
          border-left: 4px solid #feb200;
          color: white;
        }

        /* Selos com ícones na cor amarela */
        .hero-seals { padding: 24px; }
        .seal-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
        }
        .seal-item {
          display: flex;
          align-items: center;
          gap: 12px;
          color: white;
          font-size: 0.95rem;
        }
        .seal-item svg {
          width: 24px;
          height: 24px;
          stroke: #feb200;
        }

        /* Cards de resultado */
        .resultados { padding: 48px 0; flex: 1; }
        .pedido-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          margin-bottom: 24px;
          border: 1px solid #eaeaea;
        }
        .pedido-header {
          padding: 20px 24px;
          border-bottom: 1px solid #eaeaea;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .pedido-header h2 { 
          font-size: 1.125rem; 
          font-weight: 600; 
          color: #171717; 
        }
        .pedido-date { 
          font-size: 0.875rem; 
          color: #666; 
          margin-top: 4px; 
        }
        .badge {
          padding: 6px 12px;
          border-radius: 999px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }
        .status-entregue { background: #10b981; color: white; }
        .status-em_transito, .status-em_rota { background: #feb200; color: #171717; }
        .status-pendente { background: #94a3b8; color: white; }
        .status-amazon-dba { background: #ff9900; color: #171717; }
        .status-cancelado { background: #ef4444; color: white; }
        .badge-secondary { background: #64748b; color: white; }

        .amazon-dba-card {
          border-left: 4px solid #ff9900;
        }
        .amazon-dba-subtitulo {
          color: #666;
          font-weight: 500;
        }
        .amazon-dba-aviso {
          display: flex;
          gap: 16px;
          align-items: flex-start;
          padding: 20px;
          margin-bottom: 20px;
          background: #fff8eb;
          border-radius: 8px;
          border: 1px solid #ffe0a3;
        }
        .amazon-dba-aviso-icon {
          flex-shrink: 0;
          color: #ff9900;
        }
        .amazon-dba-aviso h3 {
          margin: 0 0 8px;
          font-size: 1.05rem;
          color: #171717;
        }
        .amazon-dba-aviso p {
          margin: 0 0 8px;
          color: #444;
          line-height: 1.55;
        }
        .amazon-dba-dica {
          font-size: 0.9rem !important;
          color: #666 !important;
        }

        .pedido-body { padding: 24px; }
        .tracking-section h3 {
          font-size: 0.875rem;
          font-weight: 600;
          text-transform: uppercase;
          color: #666;
          margin-bottom: 20px;
        }
        .timeline {
          position: relative;
          padding-left: 20px;
          margin-bottom: 24px;
        }
        .timeline:before {
          content: '';
          position: absolute;
          left: 6px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: #eaeaea;
        }
        .timeline-item { position: relative; padding-bottom: 24px; }
        .timeline-dot {
          position: absolute;
          left: -20px;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #ddd;
          border: 2px solid white;
        }
        .timeline-dot.success { background: #feb200; }
        .timeline-content { padding-left: 16px; }
        .timeline-date { font-size: 0.875rem; color: #666; margin-bottom: 4px; }
        .timeline-title { font-weight: 600; color: #171717; margin-bottom: 4px; }
        .timeline-description { font-size: 0.875rem; color: #444; }

        .info-resumida {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          padding-top: 16px;
          border-top: 1px solid #eaeaea;
        }
        .info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .info-label { font-size: 0.75rem; color: #666; text-transform: uppercase; }
        .info-value { font-weight: 600; color: #171717; }

        /* Footer com tema escuro */
        .footer {
          background: #171717;
          color: white;
          padding: 48px 0 24px;
        }
        .footer-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 32px;
          margin-bottom: 48px;
        }
        .footer-section h4 {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 16px;
          color: #feb200;
        }
        .footer-section p { 
          color: #ccc; 
          font-size: 0.875rem; 
          margin-bottom: 4px; 
        }
        .footer-hours { margin-top: 8px; font-style: italic; }
        .payment-methods { display: flex; gap: 8px; flex-wrap: wrap; }
        .payment-methods span {
          background: #2a2a2a;
          color: white;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 0.875rem;
        }
        .security-seals { 
          display: flex; 
          flex-direction: column; 
          gap: 4px; 
          color: #ccc; 
          font-size: 0.875rem; 
        }
        .footer-bottom {
          text-align: center;
          padding-top: 24px;
          border-top: 1px solid #2a2a2a;
          color: #ccc;
          font-size: 0.875rem;
        }

        /* Responsividade */
        @media (max-width: 768px) {
          .hero-grid { grid-template-columns: 1fr; gap: 24px; }
          .header-nav { display: none; }
          .footer-grid { grid-template-columns: repeat(2, 1fr); }
          .search-box { flex-direction: column; }
        }
        @media (max-width: 480px) {
          .footer-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default RastreioPublico;